import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import { EPDDeclareContentProductModel } from 'types/content-declaration/EPDDeclareContentProductModel';
import { EPDProductType } from 'types/types';
import { EPDProductClassificationId, hasDangerMaterialsLookup } from '../../constants';
import DangerMaterialsGridPdf from '../../grids/product-content/DangerMaterialsGridPdf';
import OtherDangerMaterialsGridPdf from '../../grids/product-content/OtherDangerMaterialsGridPdf';
import PackagingMaterialsGridPdf from '../../grids/product-content/PackagingMaterialsGridPdf';
import PanelContentGridPdf from '../../grids/product-content/PanelContentGridPdf';
import ProductComponentsGridPdf from '../../grids/product-content/ProductComponentsGridPdf';
import { IsISO21930StandardCompliant, IsPanelContentEnabled } from '../../utils';
import VersionHistorySection from '../VersionHistorySection';
import VersionHistorySummaryGrid, { SummaryGridRow, VersionHistorySummaryGridSkeleton } from '../VersionHistorySummaryGrid';
import {
  EpdVersionHistoryData,
  useEpdVersionHistoryDataOrWhileLoadingThenNull,
} from '../utils/EpdVersionHistoryDataContext';
import { ComparerRowsGetter } from '../../version-compare/VersionCompareKeyValueGrid';

const VersionHistoryContentDeclarationSection: React.FC<{}> = () => {
  const data = useEpdVersionHistoryDataOrWhileLoadingThenNull();

  if (!data) {
    return (
      <VersionHistorySection title="Content declaration">
        <VersionHistorySummaryGridSkeleton skeletonRowsCount={4} />
        <VersionHistorySummaryGridSkeleton skeletonRowsCount={4} />
        <VersionHistorySummaryGridSkeleton skeletonRowsCount={4} />
      </VersionHistorySection>
    );
  }

  const isISO21930StandardSelected = IsISO21930StandardCompliant(data);
  const isPanelContentEnabled = IsPanelContentEnabled(data.pcrInformation);

  const averageProductContentDeclaration = {
    ...(data.contentDeclarationProducts.find((x) => x.type === EPDContentDeclarationProductType.Average) ?? null),
    type: EPDContentDeclarationProductType.Average,
  };
  const bestProductContentDeclaration =
    data.contentDeclarationProducts.find((x) => x.type === EPDContentDeclarationProductType.BestProduct) ?? null;
  const worstProductContentDeclaration =
    data.contentDeclarationProducts.find((x) => x.type === EPDContentDeclarationProductType.WorstProduct) ?? null;
  const isBestCaseEnabled =
    !!bestProductContentDeclaration?.productDescription &&
    data.epdDefinitionInformation.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID;
  const isWorstCaseEnabled =
    !!worstProductContentDeclaration?.productDescription &&
    data.epdDefinitionInformation.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID;

  const renderContentDeclarationSection = ({
    title,
    product,
  }: {
    title: string;
    product: EPDDeclareContentProductModel;
  }) => {
    return (
      <VersionHistorySection title={title}>
        <VersionHistorySummaryGrid rows={getContentDeclarationHeaderRows(data, false, product)} />

        <ProductComponentsGridPdf
          epdVersionId={data.epdVersionId}
          serviceIncludesProductInSystemBoundary={data.epdDefinitionInformation.serviceIncludesProductInSystemBoundary}
          contentDeclarationType={product.type}
          isISO21930StandardSelected={isISO21930StandardSelected}
        />

        <PackagingMaterialsGridPdf
          epdVersionId={data.epdVersionId}
          contentDeclarationType={product.type}
          isISO21930StandardSelected={isISO21930StandardSelected}
        />

        {isPanelContentEnabled && (
          <PanelContentGridPdf
            epdVersionId={data.epdVersionId}
            contentDeclarationType={product.type}
            isISO21930StandardSelected={isISO21930StandardSelected}
          />
        )}

        <DangerMaterialsGridPdf epdVersionId={data.epdVersionId} contentDeclarationType={product.type} />

        <OtherDangerMaterialsGridPdf epdVersionId={data.epdVersionId} contentDeclarationType={product.type} />
      </VersionHistorySection>
    );
  };

  return (
    <>
      {renderContentDeclarationSection({
        title: 'Content declaration',
        product: averageProductContentDeclaration,
      })}

      {isBestCaseEnabled &&
        renderContentDeclarationSection({
          title: 'Content declaration - Best-case product',
          product: bestProductContentDeclaration,
        })}

      {isWorstCaseEnabled &&
        renderContentDeclarationSection({
          title: 'Content declaration - Worst-case product',
          product: worstProductContentDeclaration,
        })}
    </>
  );
};

export default VersionHistoryContentDeclarationSection;

export const getContentDeclarationHeaderRows: ComparerRowsGetter = (
  data: EpdVersionHistoryData,
  _,
  product: EPDDeclareContentProductModel
) => {
  if (product?.type !== EPDContentDeclarationProductType.Average) {
    return [
      { ord: 1, name: 'Product description', value: product.productDescription ?? NOT_ASSIGNED },
      {
        ord: 2,
        name: 'The product contains substances in the list of SVHC that constitute more than 0.1% of its weight',
        value: hasDangerMaterialsLookup.find((x) => x.value == product?.hasDangerMaterials)?.label ?? NOT_ASSIGNED,
      },
    ];
  }

  const possibleRows = [
    !!data.epdDefinitionInformation.epdClassification &&
      data.epdDefinitionInformation.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID && {
        ord: 3,
        name: 'Content declaration of multiple products',
        value: product?.productDescription ?? NOT_ASSIGNED,
      },
    data.epdDefinitionInformation.productType === EPDProductType.Services && {
      ord: 4,
      name: 'Does the service include any physical products used within the defined system boundary?',
      value: data.epdDefinitionInformation.serviceIncludesProductInSystemBoundary ?? NOT_ASSIGNED,
    },
    data.epdDefinitionInformation.serviceIncludesProductInSystemBoundary === 'No' && {
      ord: 5,
      name: 'Explanation why content declaration is not relevant',
      value: data.epdDefinitionInformation.declarationIrrelevanceExplanation ?? NOT_ASSIGNED,
    },
    {
      ord: 6,
      name: 'The product contains substances in the list of SVHC that constitute more than 0.1% of its weight',
      value: hasDangerMaterialsLookup.find((x) => x.value == product?.hasDangerMaterials)?.label ?? NOT_ASSIGNED,
    },
  ];

  return (possibleRows.filter((r) => r) as SummaryGridRow[]) || [];
};
